import { useQuery } from "@tanstack/react-query";
import { uniq } from "lodash";
import { useMemo } from "react";
import { useGeotabClient } from "@/components/GeotabClientContext";
import VinDetail from "@/models/VinDetail";
import { chunkArray } from "@/utilities/chunkArray";

/** VINs per request. A fleet-wide caller asks about tens of thousands of them at once. */
const VINS_PER_REQUEST = 5000;

export function useDecodeVins(vins?: string[]) {
  const { geotabClient, activeSessionId } = useGeotabClient();

  // Sorted, because this array is the query key: in caller order it changes whenever the same
  // vehicles arrive in a different order, and every VIN is decoded again.
  const uniqueIds = useMemo(() => (vins ? uniq(vins).sort() : []), [vins]);

  return useQuery<VinDetail[]>({
    queryKey: ["vins", activeSessionId, uniqueIds],

    queryFn: async ({ signal }) => {
      if (uniqueIds.length === 0) {
        return [];
      }

      // Chunked and requested together: one call with a fleet's worth of VINs in it fails, and
      // then no vehicle gets a decoded type rather than all of them getting one.
      const batches = await Promise.all(
        chunkArray(uniqueIds, VINS_PER_REQUEST).map(
          async batch =>
            ((await geotabClient.call("DecodeVinsNew", { vins: batch }, signal)) ??
              []) as VinDetail[],
        ),
      );

      return batches.flat();
    },
    enabled: !!uniqueIds.length,
  });
}
